import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import {
  Newspaper,
  Search,
  Plus,
  ChevronRight,
  ArrowLeft,
  Bell,
  BellOff,
  TrendingUp,
  Clock,
  Eye,
  Flame,
  Star,
} from 'lucide-react';
import { newsroomApi, ArticleSortBy } from '../services/newsroomApi';
import { ArticleCard } from './NewsroomPage';
import { useAuthStore } from '../store/authStore';
import { toast } from 'react-hot-toast';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2023 }, (_, i) => currentYear - i);

export const NewsroomChannelPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [sortBy, setSortBy] = useState<ArticleSortBy>('recent');
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  const SORT_OPTIONS: { value: ArticleSortBy; label: string; icon: React.ReactNode }[] = [
    { value: 'recent', label: t('newsroom.sortRecent'), icon: <Clock className="w-3.5 h-3.5" /> },
    { value: 'most_read', label: t('newsroom.sortMostRead'), icon: <Eye className="w-3.5 h-3.5" /> },
    { value: 'most_discussed', label: t('newsroom.sortMostDiscussed'), icon: <Flame className="w-3.5 h-3.5" /> },
    { value: 'most_liked', label: t('newsroom.sortMostLiked'), icon: <Star className="w-3.5 h-3.5" /> },
  ];

  const canPost = user?.role === 'professional' || user?.role === 'admin';

  const { data: channels = [] } = useQuery({
    queryKey: ['newsroom-channels'],
    queryFn: newsroomApi.getChannels,
  });

  const channel = channels.find((c) => c.slug === slug);

  const subscribeMutation = useMutation({
    mutationFn: () =>
      channel?.is_subscribed
        ? newsroomApi.unsubscribeChannel(channel.id)
        : newsroomApi.subscribeChannel(channel!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-channels'] });
      toast.success(channel?.is_subscribed ? t('newsroom.notificationsDisabled') : t('newsroom.notificationsEnabled'));
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['newsroom-articles-channel', slug, activeSearch, sortBy, selectedYear],
    queryFn: () =>
      newsroomApi.getArticles({
        channel_id: channel?.id,
        search: activeSearch || undefined,
        sort_by: sortBy,
        year: selectedYear,
        limit: 20,
      }),
    enabled: !!channel?.id,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchTerm);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/newsroom" className="flex items-center gap-1 hover:text-[#0B1F3A]">
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('newsroom.title')}
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-900 font-medium">{channel?.name ?? slug}</span>
      </nav>

      {channel && (
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channel.color }} />
              <h1 className="text-2xl font-bold text-gray-900">{channel.name}</h1>
            </div>
            {channel.description && <p className="text-gray-600">{channel.description}</p>}
            <p className="text-sm text-gray-400 mt-1">
              {t('newsroom.article', { count: channel.article_count })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => subscribeMutation.mutate()}
              disabled={subscribeMutation.isPending || !channel}
              title={channel?.is_subscribed ? t('newsroom.notificationsOff') : t('newsroom.notificationsOn')}
              className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                channel?.is_subscribed
                  ? 'bg-[#0B1F3A] text-white border-[#0B1F3A] hover:bg-red-600 hover:border-red-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-[#0B1F3A] hover:text-[#0B1F3A]'
              }`}
            >
              {channel?.is_subscribed ? (
                <><BellOff className="w-4 h-4" /> {t('newsroom.notificationsOff')}</>
              ) : (
                <><Bell className="w-4 h-4" /> {t('newsroom.notificationsOn')}</>
              )}
            </button>
            {canPost && (
              <Button onClick={() => navigate('/newsroom/nieuw-artikel')}>
                <Plus className="w-4 h-4 mr-2" />
                {t('newsroom.postArticle')}
              </Button>
            )}
          </div>
        </div>
      )}

      <Card className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('newsroom.searchInChannel', { channel: channel?.name ?? '' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">{t('newsroom.search')}</Button>
          {activeSearch && (
            <Button type="button" variant="outline" size="sm" onClick={() => { setActiveSearch(''); setSearchTerm(''); }}>
              {t('newsroom.clearSearch')}
            </Button>
          )}
        </form>
      </Card>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <TrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setSortBy(opt.value)}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors ${
              sortBy === opt.value
                ? 'bg-[#0B1F3A] text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A]'
            }`}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}
        <select
          value={selectedYear ?? ''}
          onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
          className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-[#0B1F3A] focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] cursor-pointer"
        >
          <option value="">{t('newsroom.allYears')}</option>
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
              <div className="aspect-video bg-gray-200" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Newspaper className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">
              {activeSearch
                ? t('newsroom.noArticlesSearch')
                : selectedYear
                ? t('newsroom.noArticlesYear', { year: selectedYear })
                : t('newsroom.noArticlesChannel')}
            </p>
            {canPost && (
              <Button className="mt-4" size="sm" onClick={() => navigate('/newsroom/nieuw-artikel')}>
                <Plus className="w-4 h-4 mr-2" />
                {t('newsroom.firstArticle')}
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
};
